import { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import sortBy from 'lodash/sortBy';
import last from 'lodash/last';
import classNames from 'classnames';
import moment from 'moment';
import {
  API,
  GlobalActions,
  Field2 as Field,
  ApproverList,
  ApproverCard,
  VibeTooltip,
  VibeIcon,
  viUser,
  color,
} from 'vibeguide';
import C from 'constants';
import Grid from '@mui/material/Grid';
import './ApproverInformation.scss';

function ApproverInformation({
  className = '',
  style = {},
  title = '',
  flightData,
  flightSegments = [],
  isOrder = false,
  onChange = () => {},
  user,
  setPanel,
}) {
  // setting for order approval requirement at the partner level
  const [partnerRequireApproval, setPartnerRequireApproval] = useState(false);

  const approverSettings = flightData.partnerApproval || {};
  const approvers = approverSettings.approvers || [];
  // approval if required if the partner requires it, or the toggle is enabled
  const requireApproval = flightData.approvalRequired || partnerRequireApproval;
  // when no segments exist on the flight/order
  const emptySegments = flightSegments.length === 0;

  // disable approver settings if user cannot modify the flight
  // showing approver information from an order
  // flight is awaiting approval
  // flight has no segments (selecting approvers requires segment information to determine available approvers)
  // or user partner ID does not match the flight partner ID
  const disabled = isOrder
    || !user.can('flight.modify')
    || flightData.status === 'awaiting-approval'
    || emptySegments
    || (user.partnerId && user.partnerId !== flightData.partnerId);

  const allowEditApprovers = !disabled && approverSettings.style && approverSettings.style !== 'anyone';
  const allowRemoveApprovers = !disabled;

  /**
   * Get the Approval Date for an Order
   * Use the last approvers timestamp to indicate when it was approved
   */
  const getApprovalDate = () => {
    const approvers = (flightData.partnerApproval?.approvers || []).filter(approver => approver.status === 'approved');
    const sortApproversByDate = sortBy(approvers, 'reviewedAt');
    const lastApprover = last(sortApproversByDate) || {};

    return moment(lastApprover.reviewedAt).format('YYYY-MM-DD');
  };

  /**
   * Change an Approver Setting
   */
  const onChangeApproverSetting = (e) => {
    const {
      target: {
        name,
        value,
      },
    } = e;

    const data = {
      [name]: value,
      // change the number of required approvers to 1 if not unanimous
      approvalsRequired: value === 'n-required'
        ? 1
        : approvers.length,
    };

    // reset approvers if option is changed to anyone
    if (name === 'style' && value === 'anyone') {
      data.approvers = [];
    }

    onChange({
      partnerApproval: {
        ...approverSettings,
        ...data,
      },
    });
  };

  /**
   * Change Publish Automatically Toggle
   */
  const onChangePublishAutomatically = (value) => {
    const data = {
      approvalRequired: !value,
    };

    // if changing to not require approval, no need to keep approver settings
    if (!data.approvalRequired) {
      data.partnerApproval = {};
    }

    onChange(data);
  };

  /**
   * Remove an Approver
   */
  const onRemoveApprover = (removeApprover) => {
    const newApprovers = approvers.filter(approver => approver._id !== removeApprover._id);

    onChange({
      partnerApproval: {
        ...approverSettings,
        approvers: newApprovers,
        // update the required approver count if all approvers are required
        approvalsRequired: approverSettings.style === 'unanimous'
          ? newApprovers.length
          : approverSettings.approvalsRequired,
      },
    });
  };

  /**
   * Close the Approver Selection Sidebar
   */
  const onCloseApprovers = () => {
    setPanel({
      show: false,
    });
  };

  /**
   * Show Approver Selection
   */
  const sidebar = () => {
    // add locations from all flight segments
    const segmentLocations = [];
    flightSegments.forEach(segment => {
      segment.locations.forEach(company => {
        company.locations.forEach(location => {
          // only add unique locations
          segmentLocations.push({
            companyId: company._id,
            locationId: location._id,
          });
        });
      });
    });

    setPanel({
      show: true,
      backdrop: true,
      width: 600,
      title: 'Select Approvers',
      children: (
        <ApproverList
          // add locations from all flight segments
          data={{
            ...flightData,
            locations: segmentLocations,
          }}
          approverType="partner"
          objectType="flight"
          onUpdate={onChange}
          onClose={onCloseApprovers}
          />
      ),
    });
  };

  /**
   * Render the approver card
   */
  const renderApproverCard = (approver, index) => {
    return (
      <div
        key={`approver-card-${index}`}
        style={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <ApproverCard
          style={{
            flexGrow: 1,
          }}
          approver={approver}
          // show waiting status only on the message request
          showWaitingStatus={flightData.status === 'awaiting-approval'}
          showIcon
        />

        {allowRemoveApprovers && (
          <div
            className="remove-approver"
            onClick={() => onRemoveApprover(approver)}
          >
            Remove
          </div>
        )}
      </div>
    );
  };

  /**
   * Render the partner approver settings field
   */
  const renderApproverSettingsField = () => {
    return (
      <Field
        type="select"
        label="Partner Approver Settings"
        name="style"
        value={approverSettings.style}
        // style={{
        //   border: flightData.approvalRequired && !approverSettings.style && `1px solid ${color.error}`,
        // }}
        options={[
          {
            label: '',
            value: '',
          },
          {
            label: 'Anyone can approve',
            value: 'anyone',
          },
          {
            label: 'All approvers must approve',
            value: 'unanimous',
          },
          {
            label: 'Only one approver must approve',
            value: 'n-required',
          },
        ]}
        onChange={onChangeApproverSetting}
        disabled={disabled}
        required
      />
    );
  };

  // Get approval requirements for partner on flight pages only
  useEffect(() => {
    if (flightData.partnerId && !isOrder) {
      async function fetchData() {
        const adConfig = await API.Partner.getAdConfig({
          _id: flightData.partnerId,
        });

        const requirePartnerApproval = adConfig.requireOrderApproval;

        // If the partner requires approval, ensure the flight is set to require approval
        if (requirePartnerApproval) {
          setPartnerRequireApproval(true);

          if (!flightData.approvalRequired) {
            // only set if not already required in the API
            onChange({ approvalRequired: true });
          }
        }
      }
      fetchData();
    }
  }, [flightData.partnerId, isOrder]);

  return (
    <VibeTooltip
      title={emptySegments ? `${C.SEGMENTS} are required before selecting approvers` : ''}
      placement="top"
    >
      <div
        className={classNames('ApproverInformation', className)}
        style={style}
      >
        {title && (
          <div
            className={classNames('ApproverInformationTitle', className)}
            style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: 16,
              ...style,
            }}
          >
            <div
              style={{
                flexGrow: 1,
                fontSize: 14,
                fontWeight: 'bold',
                color: color.primary,
              }}
            >
              {title}
            </div>
          </div>
        )}

        <Grid spacing={2} container>
          {!isOrder && (
            <Grid size={12}>
              <Field
                type="toggle"
                label="Publish Automatically"
                labelProps={{
                  tooltip: partnerRequireApproval
                    ? 'The configured settings requires approval'
                    : '',
                  tooltipPlacement: 'right',
                }}
                toggleProps={{
                  label: 'Create an order automatically, skipping the approval process.',
                }}
                value={requireApproval
                  ? 'off'
                  : 'on'}
                disabled={disabled || partnerRequireApproval}
                onChange={onChangePublishAutomatically}
              />
            </Grid>
          )}

          {requireApproval && (
            <Grid size={12}>
              <div className="approvers">
                {!isOrder && renderApproverSettingsField()}

                {allowEditApprovers && (
                  <Field
                    type="custom"
                    required={approverSettings.style !== 'anyone'}
                  >
                    <div
                      className={classNames('selected-approver', 'editable')}
                      onClick={sidebar}
                    >
                      <VibeIcon
                        style={{
                          marginRight: 8,
                        }}
                        icon={viUser}
                        color={color.primary}
                        size={16}
                      />

                      <span
                        style={{
                          fontSize: 12,
                        }}
                      >
                        Select Partner Approver
                      </span>
                    </div>
                  </Field>
                )}

                {approvers.length > 0 && (
                  <div className="approvers-assigned">
                    {approvers.filter(approver => approver.approvalType === 'override-client'
                      || approver.approvalType === 'override-global'
                      || approver.approvalType === 'override-partner')
                      .map((approver, index) => {
                        return renderApproverCard(approver, index);
                      })}

                    {approvers.filter(approver => approver.approvalType !== 'override-client'
                      && approver.approvalType !== 'override-global'
                      && approver.approvalType !== 'override-partner')
                      .map((approver, index) => {
                        return renderApproverCard(approver, index);
                      })}
                  </div>
                )}
              </div>
            </Grid>
          )}

          {isOrder && (
            <>
              <Grid size={6}>
                {renderApproverSettingsField()}
              </Grid>

              <Grid size={6}>
                <Field
                  type="datepicker"
                  label="Approval Date"
                  placeholder="YYYY-MM-DD"
                  value={getApprovalDate()}
                  dateProps={{
                    format: 'YYYY-MM-DD',
                  }}
                  disabled
                />
              </Grid>

              <Grid size={6}>
                <Field
                  type="datepicker"
                  label="Requested Date"
                  placeholder="YYYY-MM-DD"
                  value={moment(flightData.createdDate).format('YYYY-MM-DD')}
                  dateProps={{
                    format: 'YYYY-MM-DD',
                  }}
                  disabled
                />
              </Grid>
            </>
          )}

        </Grid>
      </div>
    </VibeTooltip>
  );
}

function mapStateToProps(state) {
  return {
    user: state.login.user,
  };
}

const mapDispatchToProps = {
  setPanel: GlobalActions.setPanel,
};

export default connect(mapStateToProps, mapDispatchToProps)(ApproverInformation);
